import React, { useEffect, useRef, useState } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import ImageComponent from "@project-stories/elements/Image/Image";
import { sendGtagEvent } from "frontend/js/helpers";

let hasLoaded = false;

export const PromotionTile = ({ promotion, sectionSettings }) => {
    const isMobile = useMobileBreakpoint("1023px"); // The mobile breakpoint is hardcoded based on the product grid instead of mobileBreakpoint 
    // Generate the column span class for mobile
    const getColumnSpanClass = () => {
        const mobileSpan = promotion.col_span_mobile || 1;
        const columnClass = mobileSpan > 1 ? `span-${mobileSpan}` : '';
        
        return columnClass;
    };

    const getAspectRatioClass = () => {
        const mobileSpan = promotion.col_span_mobile || 1;
        
        if (mobileSpan === 1) {
            return 'pt-[150%] lg:pt-[150%]';
        } else {
            return 'pt-[100%] lg:pt-[150%]';
        }
    };

    // Process promotional link to support various URL formats
    const getProcessedLink = () => {
        const link = promotion.link;
        
        if (!link) return '#';
        
        if (link.startsWith('http://') || link.startsWith('https://')) {
            return link;
        }
        
        if (link.startsWith('/')) {
            return link;
        }

        if (!link.includes('/') && !link.includes('.')) {
            return `/collections/${link}`;
        }

        if (link.includes('.') && !link.includes('/')) {
            return `https://${link}`;
        }

        if (link.includes('/')) {
            const parts = link.split('/');
            const type = parts[0];
            const handle = parts.slice(1).join('/');
            
            switch (type) {
                case 'collections':
                    return `/collections/${handle}`;
                case 'products':
                    return `/products/${handle}`;
                case 'pages':
                    return `/pages/${handle}`;
                case 'blogs':
                    return `/blogs/${handle}`;
                default:
                    return `/${link}`;
            }
        }
        
        return `/collections/${link}`;
    };

    const getContentAlignmentClasses = () => {
        const verticalPositionMobile = promotion.placement_text_cta_vertical || 'center';
        const verticalPositionDesktop = promotion.placement_text_cta_vertical || 'center';

        const horizontalPositionMobile = promotion.placement_text_cta_horizontal || 'center';
        const horizontalPositionDesktop = promotion.placement_text_cta_horizontal || 'center';

        // CMS uses human-readable choices: Top / Middle / Bottom (vertical)
        // and Left / Centre / Right (horizontal). Map those to Tailwind classes.
        const justifyContentClasses = {
            'Top': 'justify-start',
            'Middle': 'justify-center',
            'Bottom': 'justify-end',
            // keep legacy keys as fallback
            'start': 'justify-start',
            'center': 'justify-center',
            'end': 'justify-end'
        };

        const alignItemsClasses = {
            'Left': 'items-start',
            'Centre': 'items-center',
            'Right': 'items-end',
            // keep legacy keys as fallback
            'start': 'items-start',
            'center': 'items-center',
            'end': 'items-end'
        };

        const mobileJustifyClass = justifyContentClasses[verticalPositionMobile] || 'justify-center';
        const mobileAlignClass = alignItemsClasses[horizontalPositionMobile] || 'items-center';
        const desktopJustifyClass = justifyContentClasses[verticalPositionDesktop] || 'justify-center';
        const desktopAlignClass = alignItemsClasses[horizontalPositionDesktop] || 'items-center';

        return `${mobileJustifyClass} lg:${desktopJustifyClass} ${mobileAlignClass} lg:${desktopAlignClass}`;
    };

    const getTextAlignmentClass = () => {
        const horizontalPosition = promotion.placement_text_cta_horizontal || 'center';
        
        const textAlignClasses = {
            'Left': 'text-left',
            'Centre': 'text-center', 
            'Right': 'text-right',
            // keep legacy keys as fallback
            'start': 'text-left',
            'center': 'text-center',
            'end': 'text-right'
        };

        return textAlignClasses[horizontalPosition] || 'text-center';
    };

    // Helper to get contrast color (black or white)
    const getContrastColor = (inputColor: string) => {
        if (!inputColor) return '#ffffff';

        let r: number | null = null;
        let g: number | null = null;
        let b: number | null = null;

        const hexMatch = inputColor.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hexMatch) {
            let hex = hexMatch[1];
            if (hex.length === 3) {
                hex = hex.split('').map((c) => c + c).join('');
            }
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        } else {
            const rgbMatch = inputColor.match(/rgba?\s*\(([^)]+)\)/i);
            if (rgbMatch) {
                const parts = rgbMatch[1].split(',').map(p => p.trim());
                if (parts.length >= 3) {
                    r = parseInt(parts[0], 10);
                    g = parseInt(parts[1], 10);
                    b = parseInt(parts[2], 10);
                }
            }
        }

        if (r === null || g === null || b === null) return '#ffffff';

        // relative luminance
        const srgb = [r, g, b].map((v) => v / 255).map((c) => {
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        });
        const L = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];

        // WCAG suggests using white for dark backgrounds; threshold around 0.179
        return L > 0.179 ? '#000000' : '#ffffff';
    }

    const getOverlayOpacity = () => {
        if (!promotion.image_overlay_darken) return 0;
        if (typeof promotion.image_overlay_darken === 'number') return promotion.image_overlay_darken;
        return 0.4;
    };

    const titleStyle: any = {};
    const subtitleStyle: any = {};
    const ctaStyle: any = {};
    const fontStyle: any = {};
    const [ctaHoverApplied, setCtaHoverApplied] = useState({});

    if (promotion.title_font_size) titleStyle.fontSize = `${promotion.title_font_size}px`;
    if (promotion.tagline_font_size) subtitleStyle.fontSize = `${promotion.tagline_font_size}px`;

    // Apply colours depending on device. Mobile-specific fields should only
    // override when rendering on mobile; otherwise use the base colour fields.
    if (isMobile) {
        if (promotion.title_colour_mobile) titleStyle.color = promotion.title_colour_mobile;
        else if (promotion.title_colour) titleStyle.color = promotion.title_colour;

        if (promotion.subtitle_colour_mobile) subtitleStyle.color = promotion.subtitle_colour_mobile;
        else if (promotion.subtitle_colour) subtitleStyle.color = promotion.subtitle_colour;

        if (promotion.cta_text_colour_mobile) ctaStyle.color = promotion.cta_text_colour_mobile;
        else if (promotion.cta_text_colour) ctaStyle.color = promotion.cta_text_colour;
    } else {
        if (promotion.title_colour) titleStyle.color = promotion.title_colour;
        if (promotion.subtitle_colour) subtitleStyle.color = promotion.subtitle_colour;
        if (promotion.cta_text_colour) ctaStyle.color = promotion.cta_text_colour;
    }
    // If a CTA text colour is provided, also apply it to the CTA border
    if (isMobile) {
        ctaStyle.borderColor = promotion.cta_text_colour_mobile || promotion.cta_text_colour || ctaStyle.color;
    } else {
        ctaStyle.borderColor = promotion.cta_text_colour || ctaStyle.color;
    }
    if (promotion.font_family === 'Heading') {
        fontStyle.fontFamily = 'Saintecolombe, sans-serif';
    } else if (promotion.font_family === 'Body') {
        fontStyle.fontFamily = 'Cerapro, sans-serif';
    }

    // Set default CTA styles
    if (isMobile) {
        ctaStyle.color = promotion.cta_text_colour_mobile || promotion.cta_text_colour;
        ctaStyle.borderColor = promotion.cta_text_colour_mobile || promotion.cta_text_colour;
        ctaStyle.backgroundColor = 'transparent';
    } else {
        ctaStyle.color = promotion.cta_text_colour;
        ctaStyle.borderColor = promotion.cta_text_colour;
        ctaStyle.backgroundColor = 'transparent';
    }

    // Hover handlers: on hover, invert text and background color, border stays as original text color
    const applyCtaHover = () => {
        const style: any = {};
        // Use the current text color for background, and invert text color for contrast
        const baseColor = isMobile
            ? (promotion.cta_text_colour_mobile || promotion.cta_text_colour)
            : promotion.cta_text_colour;

        if (baseColor) {
            style.backgroundColor = baseColor;
            style.color = getContrastColor(baseColor);
            style.borderColor = baseColor;
        }

        if (Object.keys(style).length) setCtaHoverApplied(style);
    };

    const clearCtaHover = () => setCtaHoverApplied({});

    function handleClick (e) {
        e.preventDefault();

        sendGtagEvent(
            'select_promotion',
            promotion?.handle,
            promotion?.promotional_name,
            'promotional_tile',
            getProcessedLink()
        )
    }

    const activeVideo = (isMobile && promotion?.mobile_video) || promotion.video;
    const activeVideoUrl = activeVideo?.sources?.[0]?.url ?? null;
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [hasEnteredView, setHasEnteredView] = useState(false);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(
        () =>
            typeof window !== "undefined" &&
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
    const [videoFailed, setVideoFailed] = useState(false);

    useEffect(() => {
        if (typeof window.matchMedia !== "function") return;

        const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = () => setPrefersReducedMotion(mql.matches);

        onChange();

        if (typeof mql.addEventListener === "function") {
            mql.addEventListener("change", onChange);
            return () => mql.removeEventListener("change", onChange);
        } else {
            // Safari < 14 fallback
            mql.addListener(onChange);
            return () => mql.removeListener(onChange);
        }
    }, []);

    // Lazy-load + visibility tracking via IntersectionObserver.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        if (!("IntersectionObserver" in window)) {
            // No IO support: fall back to eager behaviour.
            setHasEnteredView(true);
            setIsIntersecting(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const intersecting = entries.some((entry) => entry.isIntersecting);
                setIsIntersecting(intersecting);
                if (intersecting) setHasEnteredView(true);
            },
            { threshold: 0.01 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        setVideoFailed(false);
    }, [activeVideoUrl]);

    useEffect(() => {
        if (hasEnteredView && videoRef.current) {
            videoRef.current.load();
        }
    }, [hasEnteredView, activeVideoUrl]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || prefersReducedMotion || videoFailed) return;

        if (isIntersecting && hasEnteredView) {
            video.play().catch((err) => {
                if (err?.name !== "AbortError") setVideoFailed(true);
            });
        } else {
            video.pause();
        }

        return () => video.pause();
    }, [isIntersecting, hasEnteredView, prefersReducedMotion, videoFailed, activeVideoUrl]);

    useEffect(() => {
        if (hasLoaded) return

        hasLoaded = true;

        sendGtagEvent(
            'view_promotion',
            promotion.handle,
            promotion.promotional_name,
            'promotional_tile'
        )
    })
      
    return (
        <div
            className={`promotion-tile relative ${getColumnSpanClass()}`}
            style={{color: promotion.settings?.text_colour || 'white' }}
        >
            <div className={`relative ${getAspectRatioClass()}`}>
                <div ref={containerRef} className="absolute top-0 left-0 right-0 h-full overflow-hidden">
                    {activeVideo?.sources?.length && !prefersReducedMotion && !videoFailed ? (
                        <video
                            key={activeVideo.sources?.[0]?.url || "promotion-video"}
                            ref={videoRef}
                            className={'object-cover h-full w-full'}
                            poster={isMobile ? (promotion?.mobile_image || promotion.image) : promotion.image}
                            muted
                            loop
                            playsInline
                            preload="none"
                            aria-hidden="true"
                            tabIndex={-1}
                            onError={() => setVideoFailed(true)}
                        >
                            {hasEnteredView
                                ? (activeVideo.sources || []).map((source, index) => (
                                    <source
                                        key={source.url || index}
                                        src={source.url}
                                        type={source.mime_type}
                                    />
                                ))
                                : null}
                        </video>
                    ) : (
                        <ImageComponent
                            imageUrl={
                                isMobile
                                    ? (promotion?.mobile_image || promotion.image)
                                    : promotion.image
                                }
                            alt={promotion.title || promotion.promotional_name || ""}
                            isLazy={true}
                            classes={'object-cover h-full w-full'}
                        />
                    )}
                    {/* Overlay to darken image when requested */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ backgroundColor: `rgba(0,0,0,${getOverlayOpacity()})` }}
                    />
                    <div 
                        className={`category-block__content flex flex-col h-full w-full absolute bottom-0 left-0 p-[32px] ${getContentAlignmentClasses()}`}
                        style={{
                            rowGap: isMobile 
                                ? `${sectionSettings?.promo_content_gap_mobile || promotion.settings?.content_gap_mobile || 4}px`
                                : `${sectionSettings?.promo_content_gap_desktop || promotion.settings?.content_gap_desktop || 8}px`
                        }}
                    >
                        <div className="category-block__content--subtitle">
                            <span style={{...subtitleStyle, ...fontStyle}}>{promotion.subtitle}</span>
                        </div>
                        <div className={`category-block__content--title ${getTextAlignmentClass()}`}>
                            <span style={{...titleStyle, ...fontStyle}}>{promotion.title}</span>
                        </div>
                        {promotion.cta_button_visible !== false && promotion.link && promotion.link_label &&
                            <a
                                href={getProcessedLink()}
                                className={`no-underline max-w-max button ${promotion.settings?.[isMobile ? 'button_style_mobile' : 'button_style_desktop'] || 'btn-outline'} hover:no-underline`}
                                onClick={(e) => handleClick(e)}
                                onMouseEnter={applyCtaHover}
                                onMouseLeave={clearCtaHover}
                                onFocus={applyCtaHover}
                                onBlur={clearCtaHover}
                                style={{...ctaStyle, ...ctaHoverApplied}}
                            >
                                {promotion.link_label}
                            </a>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromotionTile;
